import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useProperty } from "@/lib/property";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { BellIcon, BellSlashIcon, CheckCircleIcon, ClockIcon, PlayIcon } from "@phosphor-icons/react";
import { pushSupported, currentPermission, subscribeToPush, unsubscribeFromPush } from "@/lib/push";

const statusColor = {
  pending: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]",
  in_progress: "bg-primary/15 text-primary",
  done: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
};
const priorityColor = {
  urgent: "bg-destructive/15 text-destructive border-destructive/40",
  high: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40",
  medium: "bg-muted text-muted-foreground border-border",
  low: "bg-muted text-muted-foreground border-border",
};

// Compact colour scheme for the live room grid — matches the desktop room-status semantics
const roomTileColor = {
  available:   "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/40",
  occupied:    "bg-primary/15 text-primary border-primary/40",
  cleaning:    "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/50 animate-pulse",
  maintenance: "bg-destructive/15 text-destructive border-destructive/40",
  reserved:    "bg-secondary/15 text-secondary border-secondary/40",
  blocked:     "bg-muted text-muted-foreground border-border",
};
const roomStatusLabel = {
  available: "Ready", occupied: "In-house", cleaning: "Cleaning",
  maintenance: "Repair", reserved: "Reserved", blocked: "Blocked",
};

function LiveRoomGrid({ rooms, tasksByRoom, onTapRoom }) {
  // Group rooms by status for the legend counts
  const counts = {};
  Object.values(rooms).forEach((r) => { counts[r.status] = (counts[r.status] || 0) + 1; });
  const orderedRooms = Object.values(rooms).sort((a, b) => {
    const na = parseInt(a.number, 10), nb = parseInt(b.number, 10);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
    return (a.number || "").localeCompare(b.number || "");
  });
  return (
    <Card className="p-3 mb-4" data-testid="my-tasks-live-grid">
      <div className="flex items-center justify-between mb-2">
        <p className="label-overline">Live rooms · {orderedRooms.length}</p>
        <div className="flex gap-1.5 text-[10px]">
          {["cleaning","occupied","available"].map((k) => counts[k] ? (
            <span key={k} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border ${roomTileColor[k]}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-current" /> {counts[k]}
            </span>
          ) : null)}
        </div>
      </div>
      <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 gap-1.5" data-testid="my-tasks-grid">
        {orderedRooms.map((r) => {
          const hasTask = !!tasksByRoom[r.id];
          const cls = roomTileColor[r.status] || roomTileColor.blocked;
          return (
            <button
              key={r.id}
              onClick={() => onTapRoom && onTapRoom(r)}
              className={`relative aspect-square rounded-md border text-[10px] font-mono-plex font-semibold flex items-center justify-center active:scale-95 transition ${cls}`}
              title={`${r.number} · ${roomStatusLabel[r.status] || r.status}${hasTask ? " · task pending" : ""}`}
              data-testid={`grid-room-${r.number}`}
            >
              {r.number}
              {hasTask && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-destructive border border-background animate-pulse" data-testid={`grid-room-badge-${r.number}`} />
              )}
            </button>
          );
        })}
      </div>
    </Card>
  );
}

export default function MyTasks() {
  const { user } = useAuth();
  const { activeId, active } = useProperty();
  const [tasks, setTasks] = useState([]);
  const [rooms, setRooms] = useState({});
  const [notifState, setNotifState] = useState("default");
  const [subLoading, setSubLoading] = useState(false);
  const [tab, setTab] = useState("open"); // open | done

  const load = async () => {
    if (!activeId) return;
    const r = await api.get("/housekeeping", { params: { property_id: activeId } });
    setTasks(r.data);
    const rr = await api.get("/rooms", { params: { property_id: activeId } });
    setRooms(Object.fromEntries(rr.data.map((x) => [x.id, x])));
  };
  useEffect(() => { load(); }, [activeId]);

  useEffect(() => {
    setNotifState(currentPermission());
    // Auto-refresh every 30s to catch new tasks
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [activeId]);

  const enableAlerts = async () => {
    setSubLoading(true);
    try {
      await subscribeToPush({ role: user?.role || "housekeeping", deviceLabel: `${user?.name} · phone` });
      setNotifState("granted");
      toast.success("Mobile alerts enabled — you'll get a ping the moment a task is assigned.");
    } catch (e) {
      toast.error(e.message || "Failed to enable notifications");
      setNotifState(currentPermission());
    } finally { setSubLoading(false); }
  };

  const disableAlerts = async () => {
    setSubLoading(true);
    try {
      await unsubscribeFromPush();
      toast.success("Alerts disabled on this device");
      setNotifState(currentPermission());
    } catch (e) { toast.error(e.message || "Failed"); }
    finally { setSubLoading(false); }
  };

  const patch = async (t, patch) => {
    try {
      await api.patch(`/housekeeping/${t.id}`, patch);
      toast.success("Task updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const open = tasks.filter((t) => t.status !== "done").sort((a, b) => {
    const p = { urgent: 0, high: 1, medium: 2, low: 3 };
    return (p[a.priority] || 9) - (p[b.priority] || 9);
  });
  const done = tasks.filter((t) => t.status === "done");
  const visible = tab === "open" ? open : done;

  // Index of open task by room_id so the grid can flag rooms with pending work
  const tasksByRoom = {};
  open.forEach((t) => { if (!tasksByRoom[t.room_id]) tasksByRoom[t.room_id] = t; });
  const jumpToRoom = (room) => {
    const task = tasksByRoom[room.id];
    if (task) {
      setTab("open");
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="my-tasks-row-${task.id}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 60);
    } else {
      toast.info(`Room ${room.number} · ${roomStatusLabel[room.status] || room.status}`);
    }
  };

  return (
    <div className="pb-24 md:pb-6 max-w-2xl mx-auto" data-testid="my-tasks-page">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border -mx-4 md:mx-0 px-4 md:px-0 pb-3 pt-2 md:pt-0">
        <div className="flex items-baseline justify-between gap-2">
          <div>
            <p className="label-overline">Housekeeping · Mobile</p>
            <h1 className="font-display text-2xl md:text-3xl mt-1">My tasks</h1>
            <p className="text-xs text-muted-foreground">{active?.name}</p>
          </div>
          {pushSupported() && (
            notifState === "granted" ? (
              <Button size="sm" variant="ghost" onClick={disableAlerts} disabled={subLoading} data-testid="my-tasks-disable-alerts" className="text-[hsl(var(--success))]">
                <BellIcon size={16} weight="fill" className="mr-1" /> Alerts on
              </Button>
            ) : (
              <Button size="sm" onClick={enableAlerts} disabled={subLoading} data-testid="my-tasks-enable-alerts">
                <BellIcon size={16} className="mr-1" /> {subLoading ? "…" : "Enable alerts"}
              </Button>
            )
          )}
        </div>

        <div className="mt-3 flex gap-1">
          <button
            onClick={() => setTab("open")}
            className={`flex-1 py-2 rounded-md text-sm font-medium transition ${tab === "open" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
            data-testid="my-tasks-tab-open"
          >
            Open · {open.length}
          </button>
          <button
            onClick={() => setTab("done")}
            className={`flex-1 py-2 rounded-md text-sm font-medium transition ${tab === "done" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
            data-testid="my-tasks-tab-done"
          >
            Done today · {done.length}
          </button>
        </div>
      </div>

      <LiveRoomGrid rooms={rooms} tasksByRoom={tasksByRoom} onTapRoom={jumpToRoom} />

      <div className="space-y-3 mt-4">
        {visible.map((t) => {
          const room = rooms[t.room_id] || {};
          return (
            <Card key={t.id} className="p-4 active:scale-[0.99] transition" data-testid={`my-tasks-row-${t.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-display text-lg">Room {room.number || "?"}</p>
                    <Badge variant="outline" className={priorityColor[t.priority] || ""}>{t.priority}</Badge>
                    <Badge variant="outline" className={statusColor[t.status]}>{t.status.replace("_", " ")}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 capitalize">{t.task} · {room.room_type || "—"}</p>
                  {t.notes && <p className="text-xs mt-2 rounded bg-muted/60 p-2">{t.notes}</p>}
                  <p className="text-[10px] text-muted-foreground mt-2 font-mono-plex">Created {(t.created_at || "").slice(0, 16).replace("T", " ")}</p>
                </div>
              </div>
              {t.status !== "done" && (
                <div className="flex gap-2 mt-3">
                  {t.status === "pending" && (
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => patch(t, { status: "in_progress" })} data-testid={`my-tasks-start-${t.id}`}>
                      <PlayIcon size={14} className="mr-1" /> Start
                    </Button>
                  )}
                  <Button size="sm" className="flex-1" onClick={() => patch(t, { status: "done" })} data-testid={`my-tasks-done-${t.id}`}>
                    <CheckCircleIcon size={14} className="mr-1" /> Mark done
                  </Button>
                </div>
              )}
            </Card>
          );
        })}
        {visible.length === 0 && (
          <Card className="p-10 text-center">
            <CheckCircleIcon size={36} weight="duotone" className="mx-auto text-[hsl(var(--success))]" />
            <p className="font-display text-lg mt-2">All caught up</p>
            <p className="text-xs text-muted-foreground">
              {tab === "open" ? "No pending tasks. Enjoy the moment ☕" : "Nothing marked done yet today."}
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}
